import type { TaxonRank } from "~/api/types";

/**
 * Broadest to narrowest. Must stay in the same order as the `taxon_rank`
 * Postgres enum, which is what the server uses to reject an invalid parent.
 */
export const TAXON_RANK_ORDER: TaxonRank[] = [
  "kingdom",
  "subkingdom",
  "division",
  "subdivision",
  "class",
  "subclass",
  "superorder",
  "order",
  "suborder",
  "family",
  "subfamily",
  "tribe",
  "subtribe",
  "genus",
  "subgenus",
  "section",
  "series",
  "species",
  "subspecies",
  "variety",
  "subvariety",
  "form",
  "subform",
  "hybrid",
  "grex",
  "cultivar_group",
  "cultivar",
];

export function rankDepth(rank: TaxonRank) {
  return TAXON_RANK_ORDER.indexOf(rank);
}

export function isValidParentRank(childRank: TaxonRank, parentRank: TaxonRank) {
  return rankDepth(parentRank) < rankDepth(childRank);
}

/** Ranks that can legally sit above `rank`, nearest ancestor first. */
export function candidateParentRanks(rank: TaxonRank) {
  return TAXON_RANK_ORDER.slice(0, rankDepth(rank)).reverse();
}

/**
 * Client-side mirror of the `format_scientific_name` SQL function so a name
 * previewed while adding a plant matches what the server will store.
 */
export function formatScientificName(
  rank: TaxonRank,
  name: string,
  parentScientificName: string | null,
  isHybrid = false,
): string {
  const epithet = name.trim();
  if (!epithet) return parentScientificName ?? "";
  const parent = parentScientificName?.trim();
  if (!parent) return titleCase(epithet);

  switch (rank) {
    case "cultivar":
      return `${parent} '${epithet}'`;
    case "cultivar_group":
      return `${parent} (${epithet} Group)`;
    case "grex":
      return `${parent} ${titleCase(epithet)} gx`;
    case "subspecies":
      return `${parent} subsp. ${epithet.toLowerCase()}`;
    case "variety":
      return `${parent} var. ${epithet.toLowerCase()}`;
    case "subvariety":
      return `${parent} subvar. ${epithet.toLowerCase()}`;
    case "form":
      return `${parent} f. ${epithet.toLowerCase()}`;
    case "subform":
      return `${parent} subf. ${epithet.toLowerCase()}`;
    case "species":
      return `${parent} ${isHybrid ? "× " : ""}${epithet.toLowerCase()}`;
    case "hybrid":
      return `${parent} × ${epithet.toLowerCase()}`;
    default:
      return titleCase(epithet);
  }
}

function titleCase(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

/** "Monstera deliciosa 'Thai Constellation' — Swiss cheese plant" */
export function describeTaxon(input: {
  scientificName: string;
  commonName?: string | null;
}) {
  return input.commonName
    ? `${input.scientificName} — ${input.commonName}`
    : input.scientificName;
}
